import { useState, useMemo, useEffect } from 'react';
import {
  X,
  Plus,
  Package,
  MoreHorizontal,
  Pencil,
  ArrowRightLeft,
  RefreshCw,
  Trash2,
  Upload,
  Filter,
  AlertTriangle,
  TrendingDown,
  Activity,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  PageHeader,
  TableFilters,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  KPIGrid,
  ConfirmDialog,
  BulkUpload,
  BulkUploadColumn,
  ABCBadge,
} from '@/components/common';
import { InventoryItem, CreateInventoryItemDto, QueryInventoryParams } from '@/api/services';
import { useQueryClient } from '@tanstack/react-query';
import {
  useInventoryList,
  useInventoryDashboard,
  useCreateInventoryItem,
  useUpdateInventoryItem,
  useDeleteInventoryItem,
  useRestockInventory,
  useLocationsList,
} from '@/api/hooks';
import { queryKeys } from '@/api/queryKeys';
import { inventoryApi } from '@/api/services';
import { useAuth } from '@/context/AuthContext';
import { Separator } from '@/components/ui/separator';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { cn } from '@/lib/utils';
import { BulkEditDialog } from '@/components/inventory/BulkEditDialog';
import { BulkTransferDialog } from '@/components/inventory/BulkTransferDialog';

const statusMap: Record<string, 'optimal' | 'low' | 'stockout' | 'overstock'> = {
  Optimal: 'optimal',
  Low: 'low',
  Stockout: 'stockout',
  Overstock: 'overstock',
};

const categories = [
  'Drilling',
  'Safety',
  'Tools',
  'Parts',
  'Equipment',
  'Consumables',
  'Chemicals',
];
const units = ['EA', 'FT', 'M', 'KG', 'LB', 'GAL', 'L', 'BOX', 'SET'];

interface InventoryFormData {
  sku: string;
  name: string;
  description: string;
  category: string;
  subcategory: string;
  unit: string;
  minStockLevel: string;
  maxStockLevel: string;
  reorderPoint: string;
  leadTimeDays: string;
  unitCost: string;
  isActive: boolean;
}

const emptyFormData: InventoryFormData = {
  sku: '',
  name: '',
  description: '',
  category: 'Parts',
  subcategory: '',
  unit: 'EA',
  minStockLevel: '10',
  maxStockLevel: '',
  reorderPoint: '',
  leadTimeDays: '',
  unitCost: '',
  isActive: true,
};

/** Sentinel for the expiry filter, which is not an InventoryItem status. */
const EXPIRING_SOON = '__expiring_soon';

export default function Inventory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [abcFilter, setAbcFilter] = useState<('A' | 'B' | 'C')[]>([]);
  const [locationFilter, setLocationFilter] = useState<string[]>([]);
  const [showExpiringSoon, setShowExpiringSoon] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; item?: InventoryItem }>({
    open: false,
  });

  // Drop the selection whenever the filters change. Selected ids survive a
  // filter change but their rows do not, which would leave the bar offering to
  // edit records that are no longer on screen — the user would be acting on a
  // set they cannot see.
  useEffect(() => {
    setSelectedIds([]);
  }, [searchQuery, statusFilter, categoryFilter, abcFilter, locationFilter, showExpiringSoon]);

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem] = useState<InventoryItem | null>(null);
  const [formData, setFormData] = useState<InventoryFormData>(emptyFormData);

  // Restock modal state
  const [restockOpen, setRestockOpen] = useState(false);
  const [restockItem, setRestockItem] = useState<InventoryItem | null>(null);
  const [restockData, setRestockData] = useState({
    sourceLocationId: '',
    destinationLocationId: '',
    quantity: 1,
    unitPrice: '',
    notes: '',
  });

  // Bulk upload state
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  // Build query params
  const queryParams: QueryInventoryParams = {
    ...(searchQuery && { search: searchQuery }),
    ...(statusFilter && { status: statusFilter as InventoryItem['status'] }),
    ...(categoryFilter && { category: categoryFilter }),
    ...(abcFilter.length > 0 && { abcClassifications: abcFilter }),
    ...(locationFilter.length > 0 && { locationIds: locationFilter }),
  };

  // React Query hooks for data fetching
  const { data: inventoryData, isLoading: loading } = useInventoryList(queryParams);
  const { data: dashboardData } = useInventoryDashboard();
  const { data: locationsData, isLoading: loadingLocations } = useLocationsList({ limit: 100 });

  // Derived data from queries
  const allItems = useMemo(() => inventoryData?.data ?? [], [inventoryData]);
  const dashboard = dashboardData ?? null;
  const locations = locationsData?.data ?? [];

  // Apply client-side filter for expiring batches
  const items = useMemo(() => {
    if (!showExpiringSoon) return allItems;
    return allItems.filter((item) => (item.aggregated?.expiringBatchesCount || 0) > 0);
  }, [allItems, showExpiringSoon]);

  const stockoutRiskCount = allItems.filter(
    (item) => item.status === 'Low' || item.status === 'Stockout'
  ).length;

  // Mutations
  const createMutation = useCreateInventoryItem();
  const updateMutation = useUpdateInventoryItem();
  const deleteMutation = useDeleteInventoryItem();
  const restockMutation = useRestockInventory();

  const handleDelete = async () => {
    if (!deleteDialog.item) return;

    deleteMutation.mutate(deleteDialog.item.id, {
      onSuccess: () => {
        toast.success('Item deactivated successfully');
        setDeleteDialog({ open: false });
      },
    });
  };

  const openCreateForm = () => {
    navigate('/inventory/new');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.sku || !formData.name || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    const payload: CreateInventoryItemDto = {
      sku: formData.sku,
      name: formData.name,
      description: formData.description || undefined,
      category: formData.category,
      subcategory: formData.subcategory || undefined,
      unit: formData.unit || 'EA',
      companyId: user?.companyId || '',
      minStockLevel: formData.minStockLevel ? parseInt(formData.minStockLevel) : undefined,
      maxStockLevel: formData.maxStockLevel ? parseInt(formData.maxStockLevel) : undefined,
      reorderPoint: formData.reorderPoint ? parseInt(formData.reorderPoint) : undefined,
      leadTimeDays: formData.leadTimeDays ? parseInt(formData.leadTimeDays) : undefined,
      unitCost: formData.unitCost ? parseFloat(formData.unitCost) : undefined,
      isActive: formData.isActive,
    };

    if (editingItem) {
      updateMutation.mutate(
        { id: editingItem.id, data: payload },
        {
          onSuccess: () => {
            toast.success('Item updated successfully');
            setFormOpen(false);
          },
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toast.success('Item created successfully');
          setFormOpen(false);
        },
      });
    }
  };

  // Restock handlers
  const openRestockModal = (item: InventoryItem) => {
    setRestockItem(item);
    setRestockData({
      sourceLocationId: '',
      destinationLocationId: '',
      quantity: 1,
      unitPrice: item.unitCost?.toString() || '',
      notes: '',
    });
    setRestockOpen(true);
  };

  const handleRestock = async () => {
    if (!restockItem) return;
    if (!restockData.sourceLocationId || !restockData.destinationLocationId) {
      toast.error('Please select both source and destination locations');
      return;
    }
    if (restockData.sourceLocationId === restockData.destinationLocationId) {
      toast.error('Source and destination must be different');
      return;
    }
    if (restockData.quantity <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }

    restockMutation.mutate(
      {
        itemId: restockItem.id,
        sourceLocationId: restockData.sourceLocationId,
        destinationLocationId: restockData.destinationLocationId,
        quantity: restockData.quantity,
        unitPrice: restockData.unitPrice ? parseFloat(restockData.unitPrice) : undefined,
        notes: restockData.notes || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Restock request submitted successfully');
          setRestockOpen(false);
        },
      }
    );
  };

  // Bulk upload configuration
  const bulkUploadColumns: BulkUploadColumn[] = [
    { key: 'sku', label: 'SKU', required: true },
    { key: 'name', label: 'Name', required: true },
    { key: 'description', label: 'Description' },
    {
      key: 'category',
      label: 'Category',
      required: true,
      validate: (val) =>
        categories.includes(val) ? null : `Invalid category. Use: ${categories.join(', ')}`,
    },
    { key: 'subcategory', label: 'Subcategory' },
    {
      key: 'unit',
      label: 'Unit',
      validate: (val) =>
        !val || units.includes(val.toUpperCase()) ? null : `Invalid unit. Use: ${units.join(', ')}`,
    },
    { key: 'minStockLevel', label: 'Min Stock', type: 'number' },
    { key: 'maxStockLevel', label: 'Max Stock', type: 'number' },
    { key: 'reorderPoint', label: 'Reorder Point', type: 'number' },
    { key: 'leadTimeDays', label: 'Lead Time (Days)', type: 'number' },
    { key: 'unitCost', label: 'Unit Cost', type: 'number' },
  ];

  const handleBulkUpload = async (rows: Record<string, string>[]) => {
    let success = 0;
    let failed = 0;

    for (const row of rows) {
      try {
        const payload: CreateInventoryItemDto = {
          sku: row.sku.toUpperCase(),
          name: row.name,
          description: row.description || undefined,
          category: row.category,
          subcategory: row.subcategory || undefined,
          unit: row.unit?.toUpperCase() || 'EA',
          companyId: user?.companyId || '',
          minStockLevel: row.minStockLevel ? parseInt(row.minStockLevel) : undefined,
          maxStockLevel: row.maxStockLevel ? parseInt(row.maxStockLevel) : undefined,
          reorderPoint: row.reorderPoint ? parseInt(row.reorderPoint) : undefined,
          leadTimeDays: row.leadTimeDays ? parseInt(row.leadTimeDays) : undefined,
          unitCost: row.unitCost ? parseFloat(row.unitCost) : undefined,
        };
        await inventoryApi.create(payload);
        success++;
      } catch {
        failed++;
      }
    }

    if (success > 0) {
      toast.success(`Successfully imported ${success} items`);
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
    }
    if (failed > 0) {
      toast.error(`Failed to import ${failed} items`);
    }

    return { success, failed };
  };

  const columns: Column<InventoryItem>[] = [
    {
      key: 'name',
      header: 'Item',
      sortable: true,
      linkTo: (item) => `/inventory/${item.id}`,
      className: 'min-w-[280px]',
      // No leading icon: it was the same package glyph on every row, so it
      // distinguished nothing and cost horizontal space the name needs.
      render: (item) => (
        <div>
          <div className="font-medium">{item.name}</div>
          <div className="text-xs leading-4 text-muted-foreground">{item.sku}</div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (item) => <Badge variant="secondary">{item.category}</Badge>,
    },
    {
      key: 'abcClassification',
      header: 'ABC',
      sortable: true,
      render: (item) => <ABCBadge classification={item.abcClassification} />,
    },
    {
      key: 'totalQuantity',
      header: 'Total Stock',
      render: (item) => (
        <div>
          <div className="font-medium">
            {item.aggregated?.totalQuantity || 0} {item.unit}
          </div>
          <div className="text-xs leading-4 text-muted-foreground">
            {item.aggregated?.locationCount || 0} locations
          </div>
        </div>
      ),
    },
    {
      key: 'stockLevels',
      header: 'Min / Max',
      render: (item) => (
        // nowrap: the spaces either side of the slash were wrap opportunities,
        // so "321 / 600" broke across three lines in a narrow column.
        <span className="whitespace-nowrap font-mono text-sm text-muted-foreground">
          {item.minStockLevel} / {item.maxStockLevel || '∞'}
        </span>
      ),
    },
    {
      key: 'daysOfStock',
      header: 'Days of Stock',
      sortable: true,
      // Lives under `aggregated`, so the bare key resolves to nothing.
      accessorFn: (item) => item.aggregated?.daysOfStock,
      render: (item) => {
        const days = item.aggregated?.daysOfStock;
        if (days === null || days === undefined) {
          return <span className="text-muted-foreground text-sm">N/A</span>;
        }
        // Color code: green >60d, amber 30-60d, red <30d
        const colorClass =
          days > 60
            ? 'text-success-600 bg-success-50'
            : days >= 30
              ? 'text-warning-600 bg-warning-50'
              : 'text-danger-600 bg-danger-50';
        return <Badge className={cn('border font-mono', colorClass)}>{days}d</Badge>;
      },
    },
    {
      key: 'onOrder',
      header: 'On Order',
      render: (item) => {
        const onOrder = item.aggregated?.onOrder || 0;
        if (onOrder === 0) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }
        return (
          <div className="flex items-center gap-1.5">
            <span className="font-medium">{onOrder}</span>
            <span className="text-xs text-muted-foreground">{item.unit}</span>
          </div>
        );
      },
    },
    {
      key: 'lastMovement',
      header: 'Last Movement',
      render: (item) => {
        const lastMovement = item.aggregated?.lastMovementDate;
        if (!lastMovement) {
          return <span className="text-muted-foreground text-sm">No activity</span>;
        }
        const date = new Date(lastMovement);
        const daysAgo = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
        return (
          <div className="text-sm">
            <div className="font-medium">
              {daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo}d ago`}
            </div>
            <div className="text-xs text-muted-foreground">
              {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          </div>
        );
      },
    },
    {
      key: 'value',
      header: 'Value',
      render: (item) => (
        <span className="font-medium">
          ${((item.aggregated?.totalQuantity || 0) * (item.unitCost || 0)).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'demandVariability',
      header: 'Demand Class',
      render: (item) => {
        if (!item.demandVariability) return <span className="text-muted-foreground">—</span>;
        const demandStyles: Record<string, { bg: string; text: string; label: string }> = {
          LOW: { bg: 'bg-success-50', text: 'text-success-600', label: 'Low Variability' },
          MEDIUM: { bg: 'bg-warning-50', text: 'text-warning-600', label: 'Medium Variability' },
          HIGH: { bg: 'bg-danger-50', text: 'text-danger-600', label: 'High Variability' },
        };
        const style = demandStyles[item.demandVariability];
        return <Badge className={cn('border', style.bg, style.text)}>{style.label}</Badge>;
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => <StatusBadge status={statusMap[item.status || 'Optimal'] || 'optimal'} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (item) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/inventory/${item.id}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => openRestockModal(item)}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Restock
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialog({ open: true, item })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Deactivate
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Inventory' }]}
          title="Inventory"
          description="Stock held across all locations, with ABC class, cover in days, and quantities on order."
          actions={
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setBulkUploadOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Bulk Upload
              </Button>
              <Button variant="outline" onClick={() => setBulkTransferOpen(true)}>
                <ArrowRightLeft className="mr-2 h-4 w-4" />
                Transfer
              </Button>
              <Button onClick={openCreateForm}>
                <Plus className="mr-2 h-4 w-4" />
                Add Item
              </Button>
            </div>
          }
        />
      </motion.div>

      {/* KPI Cards */}
      {dashboard && (
        <KPIGrid columns={5}>
          <MetricCard
            label="Total Items"
            value={dashboard.metrics.totalItems}
            sentiment="info"
            icon={<Package className="h-5 w-5" />}
          />
          <MetricCard
            label="Total Value"
            value={dashboard.metrics.totalValue}
            sentiment="success"
            prefix="$"
          />
          <MetricCard
            label="Low Stock Items"
            value={dashboard.stockDistribution.low + dashboard.stockDistribution.stockout}
            sentiment="warning"
            icon={<TrendingDown className="h-5 w-5" />}
            subtitle="needs attention"
          />
          <MetricCard
            label="Stockout Risk"
            value={stockoutRiskCount}
            sentiment="danger"
            icon={<AlertTriangle className="h-5 w-5" />}
            subtitle="critical levels"
          />
          <MetricCard
            label="Active Alerts"
            value={dashboard.metrics.activeAlerts}
            sentiment="danger"
            icon={<Activity className="h-5 w-5" />}
          />
        </KPIGrid>
      )}

      <motion.div variants={fadeUp} className="mt-6">
        <DataTable
          toolbar={
            <TableFilters
              search={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search inventory..."
              filters={[
                {
                  id: 'status',
                  label: 'Status',
                  value: showExpiringSoon ? EXPIRING_SOON : statusFilter,
                  options: [
                    { value: 'Optimal', label: 'Optimal' },
                    { value: 'Low', label: 'Low Stock' },
                    { value: 'Stockout', label: 'Out of Stock' },
                    { value: 'Overstock', label: 'Overstock' },
                    { value: EXPIRING_SOON, label: 'Expiring Soon' },
                  ],
                  // Expiring Soon is a separate boolean rather than a status the
                  // API knows, so it rides in the same dropdown under a sentinel.
                  onChange: (value) => {
                    setShowExpiringSoon(value === EXPIRING_SOON);
                    setStatusFilter(value === EXPIRING_SOON ? '' : value);
                  },
                  placeholder: 'Status',
                  width: 'w-40',
                },
                {
                  id: 'category',
                  label: 'Categories',
                  value: categoryFilter,
                  options: categories.map((cat) => ({ value: cat, label: cat })),
                  onChange: setCategoryFilter,
                  placeholder: 'Category',
                  width: 'w-40',
                },
              ]}
              customFilters={
                <>
                  {/* ABC Filter Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="gap-2">
                        <Filter className="h-4 w-4" />
                        ABC Class
                        {abcFilter.length > 0 && (
                          <Badge variant="secondary" className="ml-1 rounded-sm px-1 font-normal">
                            {abcFilter.length}
                          </Badge>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      <div className="p-2 space-y-2">
                        <p className="text-xs font-medium text-muted-foreground px-2">
                          Select ABC Classes
                        </p>
                        {(['A', 'B', 'C'] as const).map((abc) => (
                          <div
                            key={abc}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-muted cursor-pointer"
                            onClick={() => {
                              setAbcFilter((prev) =>
                                prev.includes(abc) ? prev.filter((c) => c !== abc) : [...prev, abc]
                              );
                            }}
                          >
                            <div
                              className={cn(
                                'h-4 w-4 rounded border flex items-center justify-center',
                                abcFilter.includes(abc)
                                  ? 'bg-primary border-primary'
                                  : 'border-input'
                              )}
                            >
                              {abcFilter.includes(abc) && (
                                <svg
                                  className="h-3 w-3 text-primary-foreground"
                                  fill="currentColor"
                                  viewBox="0 0 12 12"
                                >
                                  <path
                                    d="M10 3L4.5 8.5L2 6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    fill="none"
                                  />
                                </svg>
                              )}
                            </div>
                            <span className="text-sm">Class {abc}</span>
                          </div>
                        ))}
                        {abcFilter.length > 0 && (
                          <>
                            <DropdownMenuSeparator />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-xs"
                              onClick={() => setAbcFilter([])}
                            >
                              Clear filter
                            </Button>
                          </>
                        )}
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Location Filter Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="gap-2">
                        <Filter className="h-4 w-4" />
                        Location
                        {locationFilter.length > 0 && (
                          <Badge variant="secondary" className="ml-1 rounded-sm px-1 font-normal">
                            {locationFilter.length}
                          </Badge>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-64 max-h-80 overflow-y-auto">
                      <div className="p-2 space-y-2">
                        <p className="text-xs font-medium text-muted-foreground px-2">
                          Select Locations
                        </p>
                        {locations.map((loc) => (
                          <div
                            key={loc.id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-muted cursor-pointer"
                            onClick={() => {
                              setLocationFilter((prev) =>
                                prev.includes(loc.id)
                                  ? prev.filter((id) => id !== loc.id)
                                  : [...prev, loc.id]
                              );
                            }}
                          >
                            <div
                              className={cn(
                                'h-4 w-4 rounded border flex items-center justify-center shrink-0',
                                locationFilter.includes(loc.id)
                                  ? 'bg-primary border-primary'
                                  : 'border-input'
                              )}
                            >
                              {locationFilter.includes(loc.id) && (
                                <svg
                                  className="h-3 w-3 text-primary-foreground"
                                  fill="currentColor"
                                  viewBox="0 0 12 12"
                                >
                                  <path
                                    d="M10 3L4.5 8.5L2 6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    fill="none"
                                  />
                                </svg>
                              )}
                            </div>
                            <span className="text-sm truncate">
                              {loc.name} <span className="text-muted-foreground">({loc.code})</span>
                            </span>
                          </div>
                        ))}
                        {locationFilter.length > 0 && (
                          <>
                            <DropdownMenuSeparator />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-xs"
                              onClick={() => setLocationFilter([])}
                            >
                              Clear filter
                            </Button>
                          </>
                        )}
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              }
            />
          }
          data={items}
          columns={columns}
          loading={loading}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          getRowId={(item) => item.id}
          onRowClick={(item) => navigate(`/inventory/${item.id}`)}
          emptyTitle="No inventory items found"
          emptyDescription="Get started by adding your first inventory item."
          emptyAction={
            <Button onClick={openCreateForm}>
              <Plus className="mr-2 h-4 w-4" />
              Add Item
            </Button>
          }
          pagination
          exportable
          columnToggle
        />
      </motion.div>

      {/* Bulk action bar — only present when something is selected. */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-xl border bg-card p-3 shadow-floating">
          <span className="pl-1 text-sm font-medium">
            {selectedIds.length} item{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <Button size="sm" onClick={() => setBulkEditOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit fields
          </Button>
          <Button size="sm" variant="outline" onClick={() => setBulkTransferOpen(true)}>
            <ArrowRightLeft className="mr-2 h-4 w-4" />
            Transfer
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
            <X className="mr-2 h-4 w-4" />
            Clear
          </Button>
        </div>
      )}

      <BulkTransferDialog
        open={bulkTransferOpen}
        onOpenChange={setBulkTransferOpen}
        // With rows selected the dialog acts on those; opened from the header
        // with nothing selected it offers everything currently listed.
        items={
          selectedIds.length > 0 ? items.filter((item) => selectedIds.includes(item.id)) : items
        }
        locations={locations}
        onTransferred={(moved) => {
          toast.success(`Transferred ${moved} item${moved > 1 ? 's' : ''}`);
          queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
          setSelectedIds([]);
        }}
      />

      <BulkEditDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        itemIds={selectedIds}
        onApplied={(result) => {
          queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
          // Keep only the rows that failed selected, so a retry after fixing
          // the cause acts on exactly those and nothing else.
          const stillFailing = result.results.filter((r) => r.status === 'failed').map((r) => r.id);
          setSelectedIds(stillFailing);
        }}
      />

      {/* Create/Edit Item Sheet */}
      <Sheet open={formOpen} onOpenChange={setFormOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingItem ? 'Edit Item' : 'Add Item'}</SheetTitle>
            <SheetDescription>
              {editingItem
                ? 'Update the inventory item details below.'
                : 'Fill in the details to create a new inventory item.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Basic Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sku">SKU *</Label>
                  <Input
                    id="sku"
                    value={formData.sku}
                    onChange={(e) =>
                      setFormData({ ...formData, sku: e.target.value.toUpperCase() })
                    }
                    placeholder="DRL-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">Unit</Label>
                  <Select
                    value={formData.unit}
                    onValueChange={(value) => setFormData({ ...formData, unit: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Drill Bit 8.5in"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter item description..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subcategory">Subcategory</Label>
                  <Input
                    id="subcategory"
                    value={formData.subcategory}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                    placeholder="e.g., PDC Bits"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Active Status</Label>
                  <p className="text-sm text-muted-foreground">
                    {formData.isActive ? 'Item is active' : 'Item is inactive'}
                  </p>
                </div>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
              </div>
            </div>

            {/* Stock Levels */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Stock Levels</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="minStockLevel">Min Stock Level</Label>
                  <Input
                    id="minStockLevel"
                    type="number"
                    value={formData.minStockLevel}
                    onChange={(e) => setFormData({ ...formData, minStockLevel: e.target.value })}
                    placeholder="10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxStockLevel">Max Stock Level</Label>
                  <Input
                    id="maxStockLevel"
                    type="number"
                    value={formData.maxStockLevel}
                    onChange={(e) => setFormData({ ...formData, maxStockLevel: e.target.value })}
                    placeholder="100"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="reorderPoint">Reorder Point</Label>
                  <Input
                    id="reorderPoint"
                    type="number"
                    value={formData.reorderPoint}
                    onChange={(e) => setFormData({ ...formData, reorderPoint: e.target.value })}
                    placeholder="20"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="leadTimeDays">Lead Time (days)</Label>
                  <Input
                    id="leadTimeDays"
                    type="number"
                    value={formData.leadTimeDays}
                    onChange={(e) => setFormData({ ...formData, leadTimeDays: e.target.value })}
                    placeholder="7"
                  />
                </div>
              </div>
            </div>

            {/* Cost */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Cost</h4>
              <div className="space-y-2">
                <Label htmlFor="unitCost">Unit Cost ($)</Label>
                <Input
                  id="unitCost"
                  type="number"
                  step="0.01"
                  value={formData.unitCost}
                  onChange={(e) => setFormData({ ...formData, unitCost: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {createMutation.isPending || updateMutation.isPending
                  ? 'Saving...'
                  : editingItem
                    ? 'Update Item'
                    : 'Create Item'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open })}
        title="Deactivate Item"
        description={`Are you sure you want to deactivate "${deleteDialog.item?.name}"? This item will no longer be available for new operations.`}
        confirmLabel="Deactivate"
        onConfirm={handleDelete}
        variant="destructive"
        loading={deleteMutation.isPending}
      />

      {/* Restock Sheet */}
      <Sheet open={restockOpen} onOpenChange={setRestockOpen}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Restock Item</SheetTitle>
            <SheetDescription>Request stock replenishment for {restockItem?.name}</SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Item Info */}
            {restockItem && (
              <div className="flex items-center gap-3 rounded-lg border p-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Package className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{restockItem.name}</p>
                  <p className="text-sm text-muted-foreground">{restockItem.sku}</p>
                </div>
              </div>
            )}

            <Separator />

            {/* Locations */}
            <div className="space-y-4">
              <h4 className="font-medium">Transfer Route</h4>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Source Location *</Label>
                  <Select
                    value={restockData.sourceLocationId}
                    onValueChange={(value) =>
                      setRestockData({ ...restockData, sourceLocationId: value })
                    }
                    disabled={loadingLocations}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} ({loc.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Destination Location *</Label>
                  <Select
                    value={restockData.destinationLocationId}
                    onValueChange={(value) =>
                      setRestockData({ ...restockData, destinationLocationId: value })
                    }
                    disabled={loadingLocations}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select destination" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name} ({loc.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Quantity & Price */}
            <div className="space-y-4">
              <h4 className="font-medium">Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={restockData.quantity}
                    onChange={(e) =>
                      setRestockData({ ...restockData, quantity: parseInt(e.target.value) || 1 })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={restockData.unitPrice}
                    onChange={(e) => setRestockData({ ...restockData, unitPrice: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={restockData.notes}
                  onChange={(e) => setRestockData({ ...restockData, notes: e.target.value })}
                  placeholder="Add any notes for this restock request..."
                  rows={3}
                />
              </div>
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setRestockOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleRestock} disabled={restockMutation.isPending}>
                {restockMutation.isPending ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Bulk Upload Sheet */}
      <Sheet open={bulkUploadOpen} onOpenChange={setBulkUploadOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Bulk Upload Inventory Items</SheetTitle>
            <SheetDescription>
              Import multiple inventory items from a CSV file. Download the template to see the
              required format.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6">
            <BulkUpload
              columns={bulkUploadColumns}
              onUpload={handleBulkUpload}
              templateFileName="inventory_items_template.csv"
              maxRows={500}
            />
          </div>
        </SheetContent>
      </Sheet>
    </motion.main>
  );
}
